import { useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  type GrantableApplication,
  type SubscriptionHistoryEntry,
  type UserSubscriptionApp,
} from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { missingGranterLabel } from '../lib/grants.js';
import { InfoBubble } from '../components/Screen.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Select } from '../components/Select.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  DotPill,
  EmptyState,
  Field,
  HoldButton,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * A person's subscriptions, and promotional grants — RidgeWill/auth#540, #567.
 *
 * ONE TABLE across every application (Jon, 2026-09-28): a row per app the
 * person holds anything in, purchased, granted, ended or lapsed. A new grant
 * starts from the panel's button and picks the app first, from every app that
 * has tiers; the tiers themselves are set on each application's Tiers tab.
 *
 * Staff never touch a PURCHASE: auth refuses a grant over a live one, so an app
 * the person is paying for is left out of the grant form, and its row says the
 * purchase is changed in the store. Change and End appear on a grant only where
 * auth says this caller may (`canEnd`): its own grant, or any with end-any.
 */
export function UserSubscriptionsTab({ userId }: { userId: string }) {
  const { state, reload } = useAsync(() => adminApi.userSubscriptions(userId), [userId]);
  const [granting, setGranting] = useState<{ appSlug?: string } | null>(null);
  const [ending, setEnding] = useState<UserSubscriptionApp | null>(null);

  return (
    <AsyncBoundary state={state}>
      {(d) => {
        // An app the person is PAYING for cannot take a grant; leave it out of the picker.
        const grantable = d.grantable.filter(
          (g) => !d.subscriptions.some((row) => row.appSlug === g.appSlug && isLivePurchase(row)),
        );
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <Panel
              title="Subscriptions"
              info={
                <InfoBubble label="About promotional grants">
                  A promotional grant gives a paid tier nobody bought: a comp, a beta tester, a staff or test
                  account. It lasts until its end date, or until it is ended here. If the person buys a tier,
                  the purchase replaces the grant. A purchase itself is never changed here; that is done in the
                  store.
                </InfoBubble>
              }
              action={
                d.canGrant ? (
                  <Button
                    disabled={grantable.length === 0}
                    title={
                      grantable.length === 0
                        ? 'No application with tiers can take a grant for this person.'
                        : undefined
                    }
                    onClick={() => setGranting({})}
                  >
                    + Grant a tier
                  </Button>
                ) : null
              }
            >
              {d.subscriptions.length === 0 ? (
                <EmptyState
                  title="No subscriptions"
                  suggestion={
                    d.canGrant ? 'Nothing bought or granted yet. Grant a tier to comp this person.' : undefined
                  }
                />
              ) : (
                <Table columns={['Application', 'Tier', 'Status', 'Source', 'Ends', 'Granted by', '']} actionsLast>
                  {d.subscriptions.map((row) => (
                    <SubscriptionRow
                      key={row.appSlug}
                      row={row}
                      canGrant={d.canGrant}
                      onChange={() => setGranting({ appSlug: row.appSlug })}
                      onEnd={() => setEnding(row)}
                    />
                  ))}
                </Table>
              )}
            </Panel>

            <History entries={d.history} />

            {granting ? (
              <GrantModal
                userId={userId}
                apps={grantable}
                initialApp={granting.appSlug}
                current={d.subscriptions}
                onClose={() => setGranting(null)}
                onDone={() => {
                  setGranting(null);
                  reload();
                }}
              />
            ) : null}
            {ending ? (
              <EndGrantModal
                userId={userId}
                app={ending}
                onClose={() => setEnding(null)}
                onDone={() => {
                  setEnding(null);
                  reload();
                }}
              />
            ) : null}
          </div>
        );
      }}
    </AsyncBoundary>
  );
}

const isLivePurchase = (row: UserSubscriptionApp) =>
  !row.grant?.inForce && (row.subscription.status === 'ACTIVE' || row.subscription.status === 'PAST_DUE');

/**
 * A tier as a person reads it. Names are free text (auth#567): one written in
 * capitals, the RevenueCat habit (`PREMIUM`), reads as "Premium"; anything else
 * is shown exactly as it was typed.
 */
function tierLabel(name: string): string {
  if (name === 'FREE') return 'Free';
  return name === name.toUpperCase() ? name.charAt(0) + name.slice(1).toLowerCase() : name;
}

/** `PAST_DUE` → "Past due", `INITIAL_PURCHASE` → "Initial purchase". */
const words = (s: string | null) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : '');

function statusPill(row: UserSubscriptionApp) {
  const { status } = row.subscription;
  if (row.grant?.inForce) return <DotPill tone="good">Promotional</DotPill>;
  if (status === 'ACTIVE') return <DotPill tone="good">Active</DotPill>;
  if (status === 'PAST_DUE') return <DotPill tone="warn">Payment retrying</DotPill>;
  if (status === 'EXPIRED') return <DotPill tone="muted">Ended</DotPill>;
  return <DotPill tone="muted">Never subscribed</DotPill>;
}

const STORE_NAMES: Record<string, string> = {
  APP_STORE: 'App Store',
  MAC_APP_STORE: 'Mac App Store',
  PLAY_STORE: 'Google Play',
  AMAZON: 'Amazon',
  STRIPE: 'Stripe',
  RC_BILLING: 'Web (RevenueCat)',
  PADDLE: 'Paddle',
  ROKU: 'Roku',
  PROMOTIONAL: 'RevenueCat promotional',
  TEST_STORE: 'Test Store',
};

function source(row: UserSubscriptionApp): string {
  if (row.grant?.inForce) return 'Promotional grant';
  if (!row.store) return 'Not recorded';
  return STORE_NAMES[row.store] ?? row.store;
}

const muted = { color: C.textMid, fontSize: '.8rem' } as const;

function SubscriptionRow({
  row,
  canGrant,
  onChange,
  onEnd,
}: {
  row: UserSubscriptionApp;
  canGrant: boolean;
  onChange: () => void;
  onEnd: () => void;
}) {
  const { subscription: sub, grant } = row;
  return (
    <tr>
      <Td top>{row.appName}</Td>
      <Td top>
        <strong>{tierLabel(sub.tier)}</strong>
        {sub.endedTier ? <div style={muted}>was {tierLabel(sub.endedTier)}</div> : null}
      </Td>
      <Td top>{statusPill(row)}</Td>
      <Td top>{source(row)}</Td>
      <Td top>
        {sub.currentPeriodEnd ? <When iso={sub.currentPeriodEnd} /> : grant?.inForce ? 'No end date' : '-'}
      </Td>
      <Td top>
        {grant ? (
          <>
            {grant.grantedBy ? (
              <a href={`#/users/${encodeURIComponent(grant.grantedBy.id)}`}>{grant.grantedBy.email}</a>
            ) : (
              missingGranterLabel(grant.source)
            )}
            <div style={muted}>
              <When iso={grant.grantedAt} />
              {grant.reason ? `: ${grant.reason}` : ''}
            </div>
          </>
        ) : (
          '-'
        )}
      </Td>
      <Td action top>
        {grant?.inForce && row.canEnd ? (
          <span style={{ display: 'inline-flex', gap: '.35rem' }}>
            <Button onClick={onChange}>Change</Button>
            <Button tone="danger" onClick={onEnd}>
              End
            </Button>
          </span>
        ) : isLivePurchase(row) && canGrant ? (
          <span style={muted}>Paid; changed in the store</span>
        ) : null}
      </Td>
    </tr>
  );
}

/**
 * Tomorrow in the READER'S time zone, as the earliest end date the input
 * accepts. Not `toISOString()`, which is UTC: in the evening in Chicago that is
 * already tomorrow, and the earliest date offered would be the day after.
 */
function tomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function GrantModal({
  userId,
  apps,
  initialApp,
  current,
  onClose,
  onDone,
}: {
  userId: string;
  apps: GrantableApplication[];
  /** Set when changing an existing grant: the app is fixed. */
  initialApp?: string;
  current: UserSubscriptionApp[];
  onClose: () => void;
  onDone: () => void;
}) {
  const heldTier = (slug: string) => current.find((r) => r.appSlug === slug && r.grant?.inForce)?.subscription.tier;
  // A NEW grant opens on an app the person holds no grant in, so "+ Grant a
  // tier" does not greet somebody with "Change the Keyodo grant".
  const [appSlug, setAppSlug] = useState(
    initialApp ?? (apps.find((a) => heldTier(a.appSlug) === undefined) ?? apps[0])?.appSlug ?? '',
  );
  const app = apps.find((a) => a.appSlug === appSlug);
  const [tier, setTier] = useState(heldTier(appSlug) ?? app?.tiers[0] ?? '');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const action = useAction();
  const changing = heldTier(appSlug) !== undefined;

  const chooseApp = (slug: string) => {
    setAppSlug(slug);
    setTier(heldTier(slug) ?? apps.find((a) => a.appSlug === slug)?.tiers[0] ?? '');
  };

  return (
    <Modal
      title={changing ? `Change the ${app?.appName ?? ''} grant` : 'Grant a tier'}
      subtitle="Recorded in the activity trail and the subscription history, with the reason."
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !app || !tier || reason.trim().length < 3}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.grantSubscription(userId, {
                    appSlug,
                    tier,
                    // The END of the chosen day, in the reader's time zone: a grant
                    // "until the 31st" should still be in force on the 31st.
                    endsAt: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null,
                    reason: reason.trim(),
                  });
                  return 'Granted.';
                })
                .then(onDone)
            }
          >
            {action.running ? 'Granting…' : changing ? 'Save grant' : 'Grant'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <div style={{ display: 'grid', gap: '.8rem' }}>
        <Field label="Application">
          <Select
            value={appSlug}
            options={apps.map((a) => ({ value: a.appSlug, label: a.appName }))}
            onChange={chooseApp}
            label="Application"
            disabled={!!initialApp}
            block
          />
        </Field>
        <Field label="Tier">
          <Select
            value={tier}
            options={(app?.tiers ?? []).map((t) => ({ value: t, label: tierLabel(t) }))}
            onChange={setTier}
            label="Tier"
            block
          />
        </Field>
        <Field label="Ends on" hint="Leave blank for no end date. The grant lasts through the end of the day you pick.">
          <input
            type="date"
            value={endDate}
            min={tomorrow()}
            onChange={(e) => setEndDate(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field
          label="Reason"
          hint="Required. Written for other staff, not for the customer: for example, beta tester, or goodwill for a support ticket."
        >
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
      </div>
    </Modal>
  );
}

function EndGrantModal({
  userId,
  app,
  onClose,
  onDone,
}: {
  userId: string;
  app: UserSubscriptionApp;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const action = useAction();

  return (
    <Modal
      title={`End the ${app.appName} grant`}
      subtitle={`They drop to Free on ${app.appName} straight away.`}
      onClose={onClose}
      width={480}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <HoldButton
            title="Hold for a second and a half to end this grant."
            disabled={action.running || reason.trim().length < 3}
            onHold={() =>
              void action
                .run(async () => {
                  await adminApi.endSubscriptionGrant(userId, { appSlug: app.appSlug, reason: reason.trim() });
                  return 'Grant ended.';
                })
                .then(onDone)
            }
          >
            {action.running ? 'Ending…' : 'End grant'}
          </HoldButton>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field label="Reason" hint="Required. Recorded with the change.">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          autoFocus
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>
    </Modal>
  );
}

/** What a history row's cause reads as. */
function cause(e: SubscriptionHistoryEntry): string {
  switch (e.source) {
    case 'grant':
      return 'Granted by staff';
    case 'grant-ended':
      return 'Grant ended by staff';
    case 'grant-expired':
      return 'Grant reached its end date';
    case 'reconcile':
      return 'Corrected from RevenueCat';
    case 'webhook':
      return e.eventType ? words(e.eventType) : 'Store event';
    default:
      return e.source;
  }
}

const tierAndStatus = (tier: string | null, status: string | null) =>
  tier === null ? 'Nothing' : `${tierLabel(tier)}, ${words(status)}`;

function History({ entries }: { entries: SubscriptionHistoryEntry[] }) {
  return (
    <Panel
      title="History"
      info={
        <InfoBubble label="What the history records">
          Every change to a tier, its status or its end date, from any cause: a store event, a
          correction from RevenueCat, or staff. History starts on the day this was released; earlier
          changes were not kept.
        </InfoBubble>
      }
    >
      {entries.length === 0 ? (
        <EmptyState title="No subscription changes recorded" />
      ) : (
        <Table columns={['When', 'App', 'Change', 'Cause', 'By', 'Reason']}>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td top>
                <When iso={e.occurredAt} />
              </Td>
              <Td top>{e.application.name}</Td>
              <Td top>
                {tierAndStatus(e.fromTier, e.fromStatus)} → {tierAndStatus(e.toTier, e.toStatus)}
              </Td>
              <Td top>
                {cause(e)}
                {e.store ? <Badge>{STORE_NAMES[e.store] ?? e.store}</Badge> : null}
              </Td>
              <Td top>
                {e.actor ? <a href={`#/users/${encodeURIComponent(e.actor.id)}`}>{e.actor.email}</a> : 'System'}
              </Td>
              <Td top>{e.reason ?? ''}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
